/*
 * Copyright (c) 2011-2019 by Curalate, Inc.
 */

import angular from 'angular';
import { PojoSet } from 'pojo-sets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { IGallery } from '@apis/galleries/galleriesApiModels';
import GalleriesCheckboxList from '@galleries/components/content/galleries-checkbox-list/GalleriesCheckboxList';
import { InstaShopDisabledMessage } from '@galleries/constants/galleriesConstants';
import GalleryContainerId from '@galleries/models/GalleryContainerId';
import { galleriesDataActions } from '@galleries/reducers/galleriesDataSlice';
import { getAllAlphabetizedGalleries } from '@galleries/selectors/getAllAlphabetizedGalleries';
import { getGalleriesForCheckboxList } from '@galleries/selectors/getGalleriesForCheckboxList';
import { isSelfServeGallery } from '@galleries/utils/galleryTypeUtils';
import { ICuralateStore } from '@models/AppState';
import Cuid from '@models/Cuid';
import { ExperienceContainerType } from '@models/ExperienceContainerType';
import { UniversalItemType } from '@models/UniversalItemType';
import makeAngReactController from '@react-bridge/angular-react-widget';
import { approveForGalleriesActions as ApproveForGalleriesActions } from '@uia/approve-for-galleries/approve-for-galleries-slice';
import { RIGHTS_STATUS } from '@apis/rights-management/rightsManagementApiModels';
import { ReduxDispatch } from 'app/curalate.store';
import getAreNoGalleriesSelected from '@uia/approve-for-galleries/getAreNoGalleriesSelected';
import { getAreNoCheckboxesSelected } from '@uia/approve-for-galleries/getAreNoCheckboxesSelected';

const mapStateToProps = (state: ICuralateStore) => {
  const galleriesData = state.galleries.data;
  const approveForGalleries = state.uiaModal.approveForGalleries;

  return {
    areNoGalleriesSelected: getAreNoGalleriesSelected(state),
    areNoCheckboxesSelected: getAreNoCheckboxesSelected(state),
    galleriesById: state.galleries.data.galleries.byId,
    alphabetizedGalleries: getAllAlphabetizedGalleries(state),
    isFetchingGalleries: galleriesData.flags.isFetchingGalleriesList,
    gallerySelection: approveForGalleries.gallerySelection,
    isBazaarvoiceSyndicationSelected:
      approveForGalleries.isBazaarvoiceSyndicationSelected,
    rightsStatus:
      state.uiaModal.selectedItem.selectedItemMetadata.rightsRequestStatus
        .status,
    rootCuid: state.uiaModal.selectedItem.selectedItemMetadata.rootCuid,
    cameFromSyndication:
      state.uiaModal.selectedItem.selectedItemMetadata.cameFromSyndication,
    galleriesForCheckboxList: getGalleriesForCheckboxList(state),
  };
};

const mapDispatchToProps = (dispatch: ReduxDispatch) => {
  return {
    galleriesData: bindActionCreators(galleriesDataActions, dispatch),
    approveForGalleriesActions: bindActionCreators(
      ApproveForGalleriesActions,
      dispatch,
    ),
  };
};

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

interface IOwnState {
  isSettingInitialCheckboxState: boolean;
}

class ApproveForGalleriesPanel extends React.Component<Props, IOwnState> {
  public constructor(props: Props) {
    super(props);

    this.state = {
      isSettingInitialCheckboxState: true,
    };
  }

  public async componentDidMount() {
    const {
      galleriesData,
      gallerySelection,
      approveForGalleriesActions: { toggleGallery, requestApprovedReels },
    } = this.props;

    // Call this to fetch galleries and set galleriesById
    await galleriesData.getGalleries();

    const disabledGalleryTypes = this.getDisabledGalleryTypes();

    // Need to access this props object directly to catch updates from fetch
    // galleries action above
    for (const gallery of this.props.galleriesForCheckboxList) {
      if (
        !isSelfServeGallery(gallery) ||
        !disabledGalleryTypes[gallery.containerType]
      ) {
        if (PojoSet.toArray(gallerySelection).length === 0) {
          toggleGallery(gallery.reelId);
        }
      }
    }
    requestApprovedReels();

    this.setState({
      isSettingInitialCheckboxState: false,
    });
  }

  public render() {
    const {
      areNoGalleriesSelected,
      areNoCheckboxesSelected,
      gallerySelection,
      isFetchingGalleries,
      isBazaarvoiceSyndicationSelected,
      approveForGalleriesActions: { toggleBazaarvoiceSyndication },
      cameFromSyndication,
    } = this.props;

    const isLoading =
      isFetchingGalleries || this.state.isSettingInitialCheckboxState;

    return (
      <div className='cu-m-3'>
        <div className='cu-text-section cu-mb-4'>Approve For Galleries</div>
        <GalleriesCheckboxList
          areNoGalleriesSelected={areNoGalleriesSelected}
          areNoCheckboxesSelected={areNoCheckboxesSelected}
          gallerySelectionState={gallerySelection}
          gallerySelected={this.handleClick}
          showHeaderText={true}
          isLoading={isLoading}
          isBazaarvoiceSyndicationSelected={isBazaarvoiceSyndicationSelected}
          toggleBazaarvoiceSyndication={toggleBazaarvoiceSyndication}
          shouldDisableBazaarvoiceSyndicationCheckbox={cameFromSyndication}
          disabledGalleryTypes={this.getDisabledGalleryTypes()}
        />
      </div>
    );
  }

  private handleClick = (galleryContainerId: GalleryContainerId) => {
    const {
      approveForGalleriesActions: { toggleGallery },
      galleriesById,
    } = this.props;

    const gallery = galleriesById[galleryContainerId] as IGallery;
    toggleGallery(gallery.reelId);
  };

  private getDisabledGalleryTypes = () => {
    const { rightsStatus, rootCuid } = this.props;
    const { itemType } = Cuid.parse(rootCuid);

    const isInstaShopDisabled =
      rightsStatus !== RIGHTS_STATUS.BRAND_OWNED ||
      itemType === UniversalItemType.MediaImport;

    if (isInstaShopDisabled) {
      return {
        [ExperienceContainerType.INSTASHOP]: InstaShopDisabledMessage,
      };
    }

    return {};
  };
}

const ConnectedApproveForGalleriesPanel = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ApproveForGalleriesPanel);

angular.module('uia').component('universalImageActionsGallery', {
  controller: makeAngReactController(<ConnectedApproveForGalleriesPanel />),
});
